import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import moment from "moment";

interface Product {
  id: string;
  uuid: string;
  productCode: string;
  serialNumber: string;
  quantity: number;
  royalty: number;
  warehouseLocationId: number;
  warehouseLocation: {
    name: string;
  };
}

interface Royalty {
  id: string;
  uuid: string;
  ddtNumber: string;
  externalId: string;
  createdAt: string;
  date: string;
  orderDateTime: string;
  shippingDateTime: string;
  customerDisplayName: string;
  carriageDescription: string;
  destinationDisplayName: string;
  shipmentSupplierCost: number;
  shipmentCost: number;
  province: string;
  products: Product[];
  warehouseId: number;
  status: string;
  location?: string;
}

interface RoyaltyTableProps {
  royalties: Royalty[];
  hasMultipleOrders: (royalty: Royalty) => boolean | any;
}

const RoyaltyTable = ({ royalties, hasMultipleOrders }: RoyaltyTableProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  const prepareTableRows = () => {
    const tableRows: JSX.Element[] = [];

    royalties.forEach((royalty) => {
      const isMultiOrder = hasMultipleOrders(royalty);
      const products = royalty.products;
      const hasMultipleProducts = products.length > 1;

      const rowsForRoyalty = products.map((product, productIndex) => {
        const isFirstProduct = productIndex === 0;
        const isLastProduct = productIndex === products.length - 1;

        return (
          <TableRow
            key={`${royalty.id}-${product.id}`}
            sx={{
              backgroundColor: isMultiOrder ? "#FFFDE7" : "inherit", // Light yellow for multiple orders
              borderTop:
                hasMultipleProducts && isFirstProduct
                  ? "2px solid #000000 !important"
                  : "none",
              borderBottom:
                hasMultipleProducts && isLastProduct
                  ? "2px solid #000000 !important"
                  : "inherit",
            }}
          >
            <TableCell component="th" scope="row" sx={{ verticalAlign: "top" }}>
              {product.productCode}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {product.serialNumber}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
              {royalty.ddtNumber}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {moment(royalty.createdAt).format("DD/MM/YYYY HH:mm")}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {moment(royalty.date).format("DD/MM/YYYY HH:mm")}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {royalty.customerDisplayName}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {royalty.destinationDisplayName}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {formatCurrency(product.royalty)}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {isLastProduct
                ? formatCurrency(royalty.shipmentSupplierCost)
                : ""}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {royalty.province}
            </TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
              {royalty.carriageDescription}
            </TableCell>
          </TableRow>
        );
      });

      tableRows.push(...rowsForRoyalty);
    });

    return tableRows;
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="royalty table">
        <TableHead>
          <TableRow>
            <TableCell>Prodotto</TableCell>
            <TableCell>Matricola</TableCell>
            <TableCell>DDT</TableCell>
            <TableCell>Data Ora Ordine</TableCell>
            <TableCell>Data Ora Evasione</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Destinazione</TableCell>
            <TableCell>Compenso Prodotto</TableCell>
            <TableCell>Costo Trasporto</TableCell>
            <TableCell>Provincia</TableCell>
            <TableCell>Descrizione Spedizione</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{prepareTableRows()}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default RoyaltyTable;
